import axiosInstance from "../../api/axiosInstance";
import { buildAuthHeaders, normalizeRawToken } from "../../api/authHeaders";

const AUTH_PATH_PREFIX = "api";

const buildPath = (path) => `${AUTH_PATH_PREFIX}/${String(path || "").replace(/^\/+/, "")}`;

const extractPayloadData = (payload) => {
  if (!payload || typeof payload !== "object") return {};
  return payload?.data?.data || payload?.data || payload;
};

export const extractStudentJwtToken = (payload) => {
  const data = extractPayloadData(payload);

  return normalizeRawToken(
    String(
      data?.jwtoken ||
        data?.jwt ||
        data?.token ||
        data?.access_token ||
        payload?.jwtoken ||
        ""
    )
  );
};

export const extractStudentEmail = (payload) => {
  const data = extractPayloadData(payload);
  return String(data?.email || payload?.email || "").trim().toLowerCase();
};

export const extractStudentPreAuthToken = (payload) => {
  const data = extractPayloadData(payload);
  return String(data?.pre_auth_token || data?.preAuthToken || "").trim();
};

export const extractStudentUserRecord = (payload, fallbackUser = {}) => {
  const data = extractPayloadData(payload);

  if (data && typeof data === "object") {
    return {
      ...fallbackUser,
      ...data,
    };
  }

  return fallbackUser;
};

export const isStudentVerified = (value) =>
  ["1", "true", "yes"].includes(String(value ?? "").trim().toLowerCase());

export const isPendingStudentVerificationResponse = (payload) => {
  const data = extractPayloadData(payload);
  const message = String(payload?.message || "").toLowerCase();

  return (
    Boolean(data?.requires_verification) ||
    message.includes("verification pending")
  );
};

export const isAlreadyVerifiedResponse = (payload) => {
  const message = String(payload?.message || "").toLowerCase();
  const data = extractPayloadData(payload);

  return isStudentVerified(data?.isVerified) || message.includes("already verified");
};

export const buildStudentDrfHeaders = () => {
  const headers = {
    "Content-Type": "application/json",
    ...buildAuthHeaders(""),
  };

  delete headers.jwt;
  delete headers["X-JWT-TOKEN"];

  return headers;
};

const post = (path, data, config = {}) =>
  axiosInstance.post(buildPath(path), data, {
    skipJwtAuth: true,
    ...config,
  });

const postV2 = (path, data, config = {}) =>
  axiosInstance.post(buildPath(`v2/${path}`), data, {
    skipJwtAuth: true,
    ...config,
  });

const normalizeEmailPasswordArgs = (emailOrPayload, password) => {
  if (emailOrPayload && typeof emailOrPayload === "object") {
    return emailOrPayload;
  }

  return {
    email: String(emailOrPayload || "").trim().toLowerCase(),
    password,
  };
};

const normalizePreAuthOtpArgs = (preAuthTokenOrPayload, otp) => {
  if (preAuthTokenOrPayload && typeof preAuthTokenOrPayload === "object") {
    return preAuthTokenOrPayload;
  }

  return {
    pre_auth_token: String(preAuthTokenOrPayload || "").trim(),
    otp: String(otp || "").trim(),
  };
};

const normalizeEmailArg = (emailOrPayload) => {
  if (emailOrPayload && typeof emailOrPayload === "object") {
    return emailOrPayload;
  }

  return {
    email: String(emailOrPayload || "").trim().toLowerCase(),
  };
};

export const studentAuthApi = {
  signup: (emailOrPayload, password) =>
    postV2("signup", normalizeEmailPasswordArgs(emailOrPayload, password)),
  signupV2: (emailOrPayload, password) =>
    postV2("signup", normalizeEmailPasswordArgs(emailOrPayload, password)),
  verifySignupOtp: (preAuthTokenOrPayload, otp) =>
    postV2(
      "verify_signup_otp",
      normalizePreAuthOtpArgs(preAuthTokenOrPayload, otp)
    ),
  login: (payload) => post("login", payload),
  checkVerificationStatus: (payload) => post("islinkVerify", payload),
  resendSignupOtp: (emailOrPayload) =>
    postV2("resend_signup_otp", normalizeEmailArg(emailOrPayload)),
  resendSignupOtpLegacy: (payload) => post("sendlink", payload),
  sendForgotPasswordLink: (payload) =>
    post("sendlinkforgetpassword", payload, {
      headers: buildStudentDrfHeaders(),
    }),
  verifyResetLink: (payload) =>
    post("verifylink", payload, {
      headers: buildStudentDrfHeaders(),
    }),
  submitNewPassword: (payload) =>
    post("forgetpassword", payload, {
      headers: buildStudentDrfHeaders(),
    }),
};
